import { useCallback } from 'react';
import { useFormContext } from '../components/FormProvider';
import { useFormValidation } from './useFormValidation';
import { useStepsConfig } from '../contexts/StepsConfigContext';
import {
  getStepOrder,
  createStepToMainStepMapping,
  getValidationRulesForStep,
  getStepTitle as getStepTitleFromConfig,
  getMainStepTitle as getMainStepTitleFromConfig,
  isStepRequiringCompletion,
  getStepsRequiringCompletion
} from '../config/eventConfigs';

export function useStepNavigation() {
  const { formData, setCurrentStep, markStepCompleted, isStepCompleted } = useFormContext();
  const stepsConfig = useStepsConfig();
  const { validateStep } = useFormValidation(stepsConfig);

  // Generate dynamic constants from config
  const STEP_ORDER = getStepOrder(stepsConfig);
  const STEP_TO_MAIN_STEP = createStepToMainStepMapping(stepsConfig);

  const getCurrentStepIndex = useCallback(() => {
    return STEP_ORDER.indexOf(formData.currentStep);
  }, [formData.currentStep, STEP_ORDER]);

  const getNextStep = useCallback(() => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex < STEP_ORDER.length - 1) {
      return STEP_ORDER[currentIndex + 1];
    }
    return null;
  }, [getCurrentStepIndex, STEP_ORDER]);

  const getPreviousStep = useCallback(() => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      return STEP_ORDER[currentIndex - 1];
    }
    return null;
  }, [getCurrentStepIndex, STEP_ORDER]);

  const canNavigateToStep = useCallback((targetStep) => {
    const targetIndex = STEP_ORDER.indexOf(targetStep);
    const currentIndex = getCurrentStepIndex();

    // Invalid step
    if (targetIndex === -1) return false;

    // Can always go back to completed steps
    if (isStepCompleted(targetStep)) {
      return true;
    }

    // Can always go to the first step
    if (targetIndex === 0) {
      return true;
    }

    // Special handling for steps that require completion
    const requiredSteps = getStepsRequiringCompletion(stepsConfig);
    
    // Check if the target step itself requires completion
    if (requiredSteps.includes(targetStep) && !isStepCompleted(targetStep)) {
      // For required steps, only allow sequential access
      if (targetIndex === currentIndex + 1 && isStepCompleted(formData.currentStep)) {
        return true;
      }
      return false;
    }

    // For non-required steps, check if any required steps are blocking access
    for (const requiredStep of requiredSteps) {
      const requiredIndex = STEP_ORDER.indexOf(requiredStep);
      
      // If there's a required step that comes before the target and hasn't been completed
      if (requiredIndex < targetIndex && !isStepCompleted(requiredStep)) {
        return false;
      }
    }

    // All other navigation is allowed (can jump freely between non-required steps)
    return true;
  }, [getCurrentStepIndex, isStepCompleted, formData.currentStep, STEP_ORDER, stepsConfig]);

  const navigateToStep = useCallback((stepId) => {
    // Check if navigation is allowed using canNavigateToStep logic
    if (STEP_ORDER.includes(stepId) && canNavigateToStep(stepId)) {
      setCurrentStep(stepId);
      
      // Scroll to top of content area after successful navigation
      const contentArea = document.querySelector('.overflow-auto');
      if (contentArea) {
        contentArea.scrollTop = 0;
      }
      
      return true;
    }
    return false;
  }, [setCurrentStep, STEP_ORDER, canNavigateToStep]);

  const goToNextStep = useCallback(() => {
    // Check if step should skip validation based on config
    const validationRules = getValidationRulesForStep(stepsConfig, formData.currentStep);
    const shouldSkipValidation = validationRules.skipValidation;
    
    if (!shouldSkipValidation && !validateStep(formData.currentStep)) {
      return false;
    }

    // Mark current step as completed
    markStepCompleted(formData.currentStep);

    const nextStep = getNextStep();
    if (nextStep) {
      setCurrentStep(nextStep);
      
      // Scroll to top of content area after successful navigation
      const contentArea = document.querySelector('.overflow-auto');
      if (contentArea) {
        contentArea.scrollTop = 0;
      }
      
      return true;
    }
    return false;
  }, [validateStep, formData.currentStep, markStepCompleted, getNextStep, setCurrentStep, stepsConfig]);

  const goToPreviousStep = useCallback(() => {
    const previousStep = getPreviousStep();
    if (previousStep) {
      setCurrentStep(previousStep);
      
      // Scroll to top of content area after successful navigation
      const contentArea = document.querySelector('.overflow-auto');
      if (contentArea) {
        contentArea.scrollTop = 0;
      }
      
      return true;
    }
    return false;
  }, [getPreviousStep, setCurrentStep]);

  const getMainStepForStep = useCallback((stepId) => {
    return STEP_TO_MAIN_STEP[stepId];
  }, [STEP_TO_MAIN_STEP]);

  const getStepsForMainStep = useCallback((mainStepId) => {
    return STEP_ORDER.filter(stepId => STEP_TO_MAIN_STEP[stepId] === mainStepId);
  }, [STEP_ORDER, STEP_TO_MAIN_STEP]);

  const isMainStepCompleted = useCallback((mainStepId) => {
    const stepsInMainStep = getStepsForMainStep(mainStepId);
    return stepsInMainStep.every(stepId => isStepCompleted(stepId));
  }, [getStepsForMainStep, isStepCompleted]);

  const getStepProgress = useCallback(() => {
    const completedCount = formData.completedSteps.length;
    const totalCount = STEP_ORDER.length;
    return {
      completed: completedCount,
      total: totalCount,
      percentage: Math.round((completedCount / totalCount) * 100)
    };
  }, [formData.completedSteps, STEP_ORDER]);

  // Dynamic helper functions
  const getStepTitle = useCallback((stepId) => {
    return getStepTitleFromConfig(stepsConfig, stepId);
  }, [stepsConfig]);

  const getMainStepTitle = useCallback((mainStepId) => {
    return getMainStepTitleFromConfig(stepsConfig, mainStepId);
  }, [stepsConfig]);

  const getValidationRules = useCallback((stepId) => {
    return getValidationRulesForStep(stepsConfig, stepId);
  }, [stepsConfig]);

  const isStepRequiringCompletionCheck = useCallback((stepId) => {
    return isStepRequiringCompletion(stepsConfig, stepId);
  }, [stepsConfig]);

  const getRequiredSteps = useCallback(() => {
    return getStepsRequiringCompletion(stepsConfig);
  }, [stepsConfig]);

  return {
    // Current state
    currentStep: formData.currentStep,
    currentStepIndex: getCurrentStepIndex(),
    
    // Navigation
    navigateToStep,
    goToNextStep,
    goToPreviousStep,
    canNavigateToStep,
    
    // Step information
    getNextStep,
    getPreviousStep,
    getMainStepForStep,
    getStepsForMainStep,
    isMainStepCompleted,
    
    // Progress
    getStepProgress,
    
    // Dynamic helpers
    getStepTitle,
    getMainStepTitle,
    getValidationRules,
    isStepRequiringCompletionCheck,
    getRequiredSteps,
    
    // Constants (now dynamic)
    STEP_ORDER,
    STEP_TO_MAIN_STEP
  };
}